import { Stack, Text } from "@mantine/core"
import { useDebouncedValue } from "@mantine/hooks"
import { PaginationState } from "@tanstack/react-table"
import { DataFetchWrapper, InfoBox, Select } from "@tfg/ui"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { RDUEComplianceStatus } from "@tfg/types/schemas/enum"
import { filtersRdueSchema } from "@tfg/types/schemas/products"

import { queryClient } from "~/backend/queryClient.ts"
import { ProductFilters } from "~/components/ProductFilters.tsx"
import { useSearchParamsFilters } from "~/components/hooks/useSearchParamsFilters"
import { ProductsRDUETAble } from "~/pages/Brand/Regulations/RDUE/ProductsRDUETable.tsx"
import { useProductsRDUEQuery } from "~/pages/Brand/Regulations/RDUE/hooks.ts"
import { useAuth } from "~/store/auth-context.tsx"

const PAGE_SIZE = 20

export const ProductsRDUEWrapper = () => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  })

  const [filtersValue, setFiltersValue] =
    useSearchParamsFilters(filtersRdueSchema)

  const [debouncedProduct] = useDebouncedValue<string | undefined>(
    filtersValue.search,
    600
  )

  const productsQuery = useProductsRDUEQuery({
    fetchDataOptions: pagination,
    filters: { ...filtersValue, search: debouncedProduct },
  })

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }))
  }, [debouncedProduct])

  const { t } = useTranslation("regulations")
  const { user, isAdmin } = useAuth()
  let adminMessage
  if (user?.organization?.settings?.rduePlan === "DISABLED") {
    if (isAdmin) {
      adminMessage =
        "You are seeing the content of this page because you are an admin. Non-admin user see a message explaining that they need to upgrade their plan to see this content. Go to the organization settings page to change the plan."
    } else {
      return (
        <InfoBox
          title={t("notSubscribed.title")}
          message={t("notSubscribed.message")}
          color="blue"
        />
      )
    }
  }

  return (
    <Stack>
      {adminMessage && (
        <Text c="violet" size="xs">
          {adminMessage}
        </Text>
      )}
      <DataFetchWrapper query={productsQuery}>
        {({ data, isFetching }) => (
          <Stack>
            <ProductFilters
              setFiltersValue={setFiltersValue}
              filtersValue={filtersValue}
              isFetching={isFetching}
              extraFilters={
                <Select
                  data={Object.entries(RDUEComplianceStatus).map(
                    ([key, value]) => ({
                      value: key,
                      label: value,
                    })
                  )}
                  w={150}
                  size="xs"
                  clearable
                  searchable
                  onChange={(value) => {
                    setFiltersValue({
                      ...filtersValue,
                      rdueCompliance: value
                        ? (value as RDUEComplianceStatus)
                        : undefined,
                    })
                    queryClient.invalidateQueries(["products", "rdue"])
                  }}
                  placeholder={t("lists.placeholders.rdueStatus")}
                />
              }
            />

            <ProductsRDUETAble
              data={data}
              fetchDataOptions={pagination}
              setPagination={setPagination}
            />
          </Stack>
        )}
      </DataFetchWrapper>
    </Stack>
  )
}
